import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import { ConfigCatProvider, createConsoleLogger, DataGovernance, LogLevel } from 'configcat-react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { Outlet } from 'react-router';

import { ErrorBoundary } from '../components/ErrorBoundary';
import { DEFAULT_LOCALE } from '../configuration/lang/lang';
import { useDisplayMessages, useLocale } from '../configuration/lang/langSlice';
import {
    hideSessionExpiredDialog,
    useIsSessionExpired,
    useSessionExpiredAcknowledged,
} from '../configuration/login/loginSlice';
import { useAppDispatch } from '../configuration/setup/hooks';
import { store } from '../configuration/setup/store';
import { useAccessToken } from '../configuration/tokenHandling/tokenSlice';
import DefaultRedirect from '../routes/DefaultRedirect';

const App = () => {
    const dispatch = useAppDispatch();

    const accessToken = useAccessToken();
    const displayMessages = useDisplayMessages();
    const userLocale = useLocale();
    const isSessionExpired = useIsSessionExpired();
    const sessionExpiredAcknowledged = useSessionExpiredAcknowledged();

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog());
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ConfigCatProvider
                sdkKey='configcat-sdk-1/iLfcCMAtjEyICYJDHb6fhg/9l4CgqIJUEKxDWb2HbJdqQ'
                options={{
                    pollIntervalSeconds: 2 * 86400, // 2 days to save a lot of wasted requests - remove this parameter in times where you do frequent changes to a feature flag
                    logger: createConsoleLogger(LogLevel.Info),
                    dataGovernance: DataGovernance.EuOnly,
                }}
            >
                <>
                    <SessionExpiredDialog
                        locale={userLocale}
                        onClose={handleSessionExpiredDialogClose}
                        show={showSessionExpired}
                    />
                    <Outlet />
                    <input data-semantic-id='access-token' type='hidden' value={accessToken || ''} />
                </>
            </ConfigCatProvider>
        </IntlProvider>
    );
};

export const AppContainer = () => (
    <ErrorBoundary>
        <DefaultRedirect />
        <Provider store={store}>
            <App />
        </Provider>
    </ErrorBoundary>
);
